import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { LangService } from 'src/app/service/lang.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

/**
 * The picker library builds its preset list by cloning the dayjs objects handed
 * to it and calling .utc() on the clone. dayjs recognises objects from another
 * of its own copies, so the clone comes back from this instance rather than the
 * library's - which means the plugin has to be registered here, or renderRanges
 * throws on the first preset. That throw lands mid-way through the directive's
 * input pass, so everything after [ranges] is dropped too and the panel opens
 * with no presets, no calendar and a stray Apply button.
 */
dayjs.extend(utc);

@Component({
  selector: 'app-analytics-date-range-picker',
  templateUrl: './analytics-date-range-picker.component.html',
  styleUrls: ['./analytics-date-range-picker.component.scss'],
})
export class AnalyticsDateRangePickerComponent implements OnChanges, OnDestroy {
  @Input() selected: any = { startDate: null, endDate: null };
  @Input() dir: string = 'ltr';
  @Input() label: string = '';
  @Input() inputId: string = 'analytics-daterange';
  @Input() inputClass: string = 'block h-[40px] w-[185px] px-8 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary focus:border-primary';
  @Input() placeholder: string = 'Select Date';
  /** left | right | center | auto — left keeps a right-edge filter on screen when Custom opens. */
  @Input() opens: string = 'auto';

  @Output() rangeApply = new EventEmitter<any>();
  @Output() rangeClear = new EventEmitter<void>();

  ranges: Record<string, [Dayjs, Dayjs]> = {};
  locale: {
    format: string;
    displayFormat?: string;
    direction?: string;
    separator: string;
    applyLabel: string;
    cancelLabel: string;
    customRangeLabel: string;
  } = {
    format: 'DD-MM-YYYY',
    separator: ' - ',
    applyLabel: 'Apply',
    cancelLabel: 'Cancel',
    customRangeLabel: 'Custom',
  };

  private sub?: Subscription;

  constructor(
    private langser: LangService,
    private translate: TranslateService,
  ) {
    this.buildRanges();
    this.sub = this.langser.getData().subscribe(() => {
      this.buildRanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected']) {
      this.normalizeSelectedToAllRange();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private normalizeSelectedToAllRange(): void {
    const noSelection = !this.selected?.startDate && !this.selected?.endDate;
    const legacyAll = AnalyticsDateRangePickerComponent.isLegacyAllRange(this.selected?.startDate, this.selected?.endDate);
    if (noSelection || legacyAll) {
      const all = AnalyticsDateRangePickerComponent.allRange();
      this.selected = { startDate: all[0], endDate: all[1] };
    }
  }

  private static allRange(): [Dayjs, Dayjs] {
    return [dayjs().startOf('year'), dayjs()];
  }

  private static isAllRange(startDate: Dayjs | null, endDate: Dayjs | null): boolean {
    if (!startDate || !endDate) return true;
    const startStr = dayjs(startDate).format('YYYY-MM-DD');
    const endStr = dayjs(endDate).format('YYYY-MM-DD');
    const all = AnalyticsDateRangePickerComponent.allRange();
    return startStr === all[0].format('YYYY-MM-DD') && endStr === all[1].format('YYYY-MM-DD');
  }

  private static isLegacyAllRange(startDate: any, endDate: any): boolean {
    if (!startDate || !endDate) return false;
    const startStr = dayjs(startDate).format('YYYY-MM-DD');
    const endStr = dayjs(endDate).format('YYYY-MM-DD');
    return startStr === '1970-01-01' && endStr === '2099-12-31';
  }

  private buildRanges(): void {
    const allRange = AnalyticsDateRangePickerComponent.allRange();
    this.ranges = {
      'All': allRange,
      'Today': [dayjs(), dayjs()],
      'Last 7 Days': [dayjs().subtract(6, 'days'), dayjs()],
      'Last 30 Days': [dayjs().subtract(29, 'days'), dayjs()],
      'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
      'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
    };
    this.locale = {
      ...this.locale,
      direction: this.dir || 'ltr',
      applyLabel: 'Apply',
      cancelLabel: 'Cancel',
      customRangeLabel: 'Custom',
    };

    this.normalizeSelectedToAllRange();
  }

  onApply(event: any): void {
    if (!event?.startDate || !event?.endDate) return;
    const isAll = AnalyticsDateRangePickerComponent.isAllRange(
      dayjs(event.startDate),
      dayjs(event.endDate)
    );
    if (isAll) {
      this.rangeApply.emit({ startDate: null, endDate: null });
    } else {
      this.rangeApply.emit(event);
    }
  }

  onClear(): void {
    const all = AnalyticsDateRangePickerComponent.allRange();
    this.selected = { startDate: all[0], endDate: all[1] };
  }

  openPicker(input: HTMLInputElement): void {
    input.focus();
    input.click();
  }
}
